import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React from "react";
import Tooltipable from "~/components/effects/tooltipable";
import {
  EDITOR_TYPES,
  FORMAT_STORAGE_KEY,
  OUTPUT_FORMATS,
} from "~/constants/EditorTypes";
import { captureEvent } from "~/helpers/capture_posthog_event";

const FORMAT_OPTIONS = [
  {
    value: OUTPUT_FORMATS.TEXT,
    label: "Text",
    icon: ["far", "fa-align-left"],
    bestFor: "SMS, notes, and short messages",
    description: "Plain text without formatting.",
  },
  {
    value: OUTPUT_FORMATS.HTML,
    label: "HTML",
    icon: ["far", "fa-code"],
    bestFor: "Emails and landing pages",
    description: "Rich formatted output with bold, lists, and links.",
  },
];

const OutputFormatToggle = ({
  selectedFormat,
  onFormatChange,
  disabled,
  editorType,
  isTemplateSet = false,
}) => {
  const tooltipContent = (
    <div className="text-left" style={{ maxWidth: 280 }}>
      <strong>Output Format</strong>
      <hr className="my-1 border-light" />
      {FORMAT_OPTIONS.map((option, idx) => (
        <div key={option.value} className={idx > 0 ? "mt-2" : undefined}>
          <div>
            <FontAwesomeIcon icon={option.icon} className="mr-1" />
            <strong>{option.label}</strong>
            {option.value === selectedFormat && (
              <span className="ml-1 text-success">(current)</span>
            )}
          </div>
          <div className="small">
            <em>{option.bestFor}.</em> {option.description}
          </div>
        </div>
      ))}
    </div>
  );

  const handleFormatChange = (newFormat) => {
    if (newFormat !== selectedFormat) {
      // BK-3229 R4 AC 3.1.3 — skip localStorage while template-driven.
      if (!isTemplateSet) {
        localStorage.setItem(FORMAT_STORAGE_KEY, newFormat);
      }
      captureEvent("ai_assist_format_toggled", {
        from_format: selectedFormat,
        to_format: newFormat,
        editor_type: editorType,
        channel: EDITOR_TYPES[editorType]?.channel,
      });
    }
    onFormatChange(newFormat);
  };

  return (
    <div className="d-flex align-items-center flex-nowrap">
      <div
        className="btn-group btn-group-sm flex-row flex-shrink-0"
        role="group"
        aria-label="Output format"
      >
        <button
          type="button"
          className={`btn btn-sm py-0 px-2 ${selectedFormat === OUTPUT_FORMATS.TEXT ? "btn-success" : "btn-outline-success"}`}
          onClick={() => handleFormatChange(OUTPUT_FORMATS.TEXT)}
          disabled={disabled}
        >
          Text
        </button>
        <button
          type="button"
          className={`btn btn-sm py-0 px-2 ${selectedFormat === OUTPUT_FORMATS.HTML ? "btn-success" : "btn-outline-success"}`}
          onClick={() => handleFormatChange(OUTPUT_FORMATS.HTML)}
          disabled={disabled}
        >
          HTML
        </button>
      </div>

      <Tooltipable trigger="click" placement="right" text={tooltipContent}>
        <FontAwesomeIcon
          icon={["fal", "fa-info-circle"]}
          className="ml-2 text-secondary text-xs cursor-pointer"
        />
      </Tooltipable>
    </div>
  );
};

OutputFormatToggle.propTypes = {
  selectedFormat: PropTypes.string.isRequired,
  onFormatChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  editorType: PropTypes.string,
  isTemplateSet: PropTypes.bool,
};

export default OutputFormatToggle;
